import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../common/axios";
import { getErrors, toastDefault } from "../../utils/handler.utils";
import Loader from "../Loader/Loader";

const MAX_TOTAL_WEIGHT = 1000;

const getEmptyOption = () => ({ name: "", weight: "", value: "" });

function AddSpin() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("free");
  const [isActive, setIsActive] = useState(false);
  const [options, setOptions] = useState([getEmptyOption()]);

  const totalWeight = useMemo(() => {
    return options.reduce((sum, option) => sum + Number(option.weight || 0), 0);
  }, [options]);

  const updateOption = (index, key, value) => {
    setOptions((prev) =>
      prev.map((option, idx) => {
        if (idx !== index) return option;
        return { ...option, [key]: value };
      })
    );
  };

  const addOption = () => {
    setOptions((prev) => [...prev, getEmptyOption()]);
  };

  const removeOption = (index) => {
    setOptions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const createSpinHandler = (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Name is required", toastDefault);
      return;
    }

    if (!options.length) {
      toast.error("At least one option is required", toastDefault);
      return;
    }

    const hasInvalidOption = options.some(
      (option) =>
        !option.name.trim() || option.weight === "" || option.value === ""
    );

    if (hasInvalidOption) {
      toast.error("All options need a name, weight, and value", toastDefault);
      return;
    }

    if (totalWeight > MAX_TOTAL_WEIGHT) {
      toast.error(
        `Total weight cannot exceed ${MAX_TOTAL_WEIGHT}`,
        toastDefault
      );
      return;
    }

    setLoading(true);

    const payload = {
      name: name.trim(),
      type,
      isactive: isActive ? 1 : 0,
      options: options.map((option) => ({
        name: option.name.trim(),
        weight: Number(option.weight),
        value: Number(option.value),
      })),
    };

    axiosInstance
      .post("/admin/spin/create", payload)
      .then(() => {
        toast.success("Spin created successfully", toastDefault);
        setTimeout(() => {
          history.push("/spin");
        }, 1500);
      })
      .catch((err) => {
        toast.error(getErrors(err, false, true), toastDefault);
        setLoading(false);
      });
  };

  return (
    <section className="relative container_admin">
      <div className="bg-white overflow-hidden rounded">
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-lg font-bold text-black">Create new spin</h3>
          <Link
            className="cstm_btn_small !bg-slate-600 hover:!bg-slate-700"
            to="/spin/available-date"
          >
            Spin dates
          </Link>
        </div>
        <div className="py-10 px-4">
          <div className="w-full md:w-[70%] mx-auto py-6 relative border border-gray-200 px-4">
            {loading && <Loader absolute />}
            <form onSubmit={createSpinHandler}>
              <div>
                <div className="form_grid">
                  <div>
                    <label htmlFor="name">Name</label>
                    <input
                      id="name"
                      className="form_input"
                      type="text"
                      placeholder="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="type">Type</label>
                    <select
                      id="type"
                      className="form_input"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      required
                    >
                      <option value="free">Free Spin</option>
                      <option value="order">Order Spin</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center">
                      <input
                        id="is_active"
                        value="1"
                        className="form-checkbox"
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      <span className="ml-2">Is Active</span>
                    </label>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-base font-semibold text-black">
                      Spin options
                    </h4>
                    <button
                      type="button"
                      className="cstm_btn_small"
                      onClick={addOption}
                    >
                      Add option
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Total weight: {totalWeight} / {MAX_TOTAL_WEIGHT}
                  </p>
                  <div className="space-y-3 mt-4">
                    {options.map((option, index) => (
                      <div
                        key={`spin-option-${index}`}
                        className="form_grid items-end"
                      >
                        <div>
                          <label htmlFor={`option-name-${index}`}>Name</label>
                          <input
                            id={`option-name-${index}`}
                            className="form_input"
                            type="text"
                            placeholder="Option name"
                            value={option.name}
                            onChange={(e) =>
                              updateOption(index, "name", e.target.value)
                            }
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor={`option-weight-${index}`}>
                            Weight
                          </label>
                          <input
                            id={`option-weight-${index}`}
                            className="form_input"
                            type="number"
                            min="0"
                            max={MAX_TOTAL_WEIGHT}
                            placeholder="Weight"
                            value={option.weight}
                            onChange={(e) =>
                              updateOption(index, "weight", e.target.value)
                            }
                              required
                            />
                        </div>
                        <div>
                          <label htmlFor={`option-value-${index}`}>Value</label>
                          <input
                            id={`option-value-${index}`}
                            className="form_input"
                            type="number"
                            min="0"
                            placeholder="Value"
                            value={option.value}
                            onChange={(e) =>
                              updateOption(index, "value", e.target.value)
                            }
                            required
                          />
                        </div>
                        <div className="flex items-end">
                          {options.length > 1 && (
                            <button
                              type="button"
                              className="cstm_btn_small !bg-red-600 hover:!bg-red-700"
                              onClick={() => removeOption(index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <button type="submit" className="cstm_btn w-full block">
                    Create spin
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AddSpin;
